import type { BatchListRow } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { Badge } from '../../atoms/Badge.tsx'
import type { Column } from '../../atoms/DataTable.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { batchStatusRank } from '../sortRanks.ts'

/**
 * 「상태」 열 — 작업 목록(진행 중)과 이력이 **같은 함수**를 쓴다(두 표가 같은 값을 다르게 말하지 않게 ·
 * eric 2026-09-30 「진행 중 탭 컬럼도.. 이력 컬럼에서 소요 시간 빼고 동일하게」 · 이력의 머리글 「결과 → 상태」).
 *
 * 값은 `Batch.status` 의 알약이다. 머리글 정렬은 **삶의 차례**(`batchStatusRank` — 글자 순 아님).
 * 열 `key` 는 `status` 다(`listColumnPriority.ts` 에 없다 — 판단에 쓰는 열이라 끝까지 남는다).
 *
 * 🔴 **멈춘 까닭은 진행 중 목록에만 붙는다**(`withHalt`). 진행 중 목록은 `error` 행에 「복구 대기」 표지와
 *    `halt_reason` 한 줄을 알약 옆에 적는다 — 서버가 「왜 멈췄나」를 안다(백엔드 `AH-17` · 2026-09-20 까지 화면이
 *    이 값을 받고도 버렸다). 이력은 종착(완료 · 중지)만 있어 알약 하나다(종전 이력 「결과」 칸 그대로).
 */
export function batchStatusColumn(options: { withHalt?: boolean } = {}): Column<BatchListRow> {
  const withHalt = options.withHalt === true
  return {
    key: 'status',
    header: t('column.status'),
    nowrap: true,
    // 이력은 알약 하나라 내용 폭 · 진행 중은 사유 글자가 붙어 자동 배분(종전 두 표 그대로)
    ...(withHalt ? {} : { width: 'fit' as const }),
    // 🔴 삶의 차례 — 내림차순이면 진행 중 목록은 오류(복구 대기), 이력은 중지가 위다(`sortRanks.ts`)
    sort: (a, b) => batchStatusRank(a.status) - batchStatusRank(b.status),
    cell: (row) =>
      withHalt ? (
        <span className="btnrow">
          <StatusBadge
            label={enumLabel('enum.batchStatus', row.status)}
            size="pill"
            status={row.status}
          />
          {row.status === 'error' ? (
            <Badge variant="inconclusive">{t('tab.batches.recover')}</Badge>
          ) : null}
          {/* ⚠ 한 줄 고정이다. 전문은 툴팁과 상세 화면에서 본다 */}
          {row.halt_reason === null ? null : (
            <span className="muted clip" title={row.halt_reason}>
              {row.halt_reason}
            </span>
          )}
        </span>
      ) : (
        <StatusBadge
          label={enumLabel('enum.batchStatus', row.status)}
          size="pill"
          status={row.status}
        />
      ),
  }
}
